import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Clock, Users } from 'lucide-react';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface MeetingSchedulerProps {
  onClose: () => void;
  onScheduled: () => void;
  editMeeting?: any;
}

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

export function MeetingScheduler({ onClose, onScheduled, editMeeting }: MeetingSchedulerProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    meeting_type: 'observation',
    custom_meeting_type: '',
    observee_id: '',
    scheduled_date: new Date(),
    scheduled_time: '',
    location: '',
    description: '',
    follow_up_required: false,
    follow_up_date: null as Date | null
  });
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [saving, setSaving] = useState(false);
  const [showFollowUpCalendar, setShowFollowUpCalendar] = useState(false);
  const [participantSearch, setParticipantSearch] = useState('');
  const [showParticipantResults, setShowParticipantResults] = useState(false);
  const [selectedParticipant, setSelectedParticipant] = useState<Profile | null>(null);

  useEffect(() => {
    loadProfiles();
    if (editMeeting) {
      setFormData({
        title: editMeeting.title || '',
        meeting_type: editMeeting.meeting_type || 'observation',
        custom_meeting_type: '',
        observee_id: editMeeting.observee_id || '',
        scheduled_date: new Date(editMeeting.scheduled_date),
        scheduled_time: editMeeting.scheduled_date ? 
          format(new Date(editMeeting.scheduled_date), 'HH:mm') : '',
        location: editMeeting.location || '',
        description: editMeeting.description || '',
        follow_up_required: editMeeting.follow_up_required || false,
        follow_up_date: editMeeting.follow_up_date ? new Date(editMeeting.follow_up_date) : null
      });

      // Find and set the selected participant for editing
      if (editMeeting.observee_id) {
        const participant = profiles.find(p => p.id === editMeeting.observee_id);
        if (participant) {
          setSelectedParticipant(participant);
          setParticipantSearch(`${participant.first_name} ${participant.last_name} (${participant.email})`);
        }
      }
    }
  }, [editMeeting, profiles]);

  const loadProfiles = async (searchQuery = '') => {
    try {
      let query = supabase
        .from('profiles')
        .select('id, first_name, last_name, email')
        .order('last_name');

      if (searchQuery.trim()) {
        query = query.or(`first_name.ilike.%${searchQuery}%,last_name.ilike.%${searchQuery}%,email.ilike.%${searchQuery}%`);
      }

      // Limit results to prevent overwhelming the UI
      query = query.limit(50);

      const { data, error } = await query;

      if (error) throw error;
      setProfiles(data || []);
    } catch (error) {
      console.error('Error loading profiles:', error);
      toast.error('Failed to load user profiles');
    }
  };

  // Handle participant search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (participantSearch.trim().length >= 2) {
        loadProfiles(participantSearch);
        setShowParticipantResults(true);
      } else if (participantSearch.trim().length === 0) {
        setShowParticipantResults(false);
        setProfiles([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [participantSearch]);

  const handleParticipantSelect = (profile: Profile) => {
    setSelectedParticipant(profile);
    setFormData(prev => ({ ...prev, observee_id: profile.id }));
    setParticipantSearch(`${profile.first_name} ${profile.last_name} (${profile.email})`);
    setShowParticipantResults(false);
  };

  const filteredProfiles = profiles.filter(profile => 
    profile.first_name.toLowerCase().includes(participantSearch.toLowerCase()) ||
    profile.last_name.toLowerCase().includes(participantSearch.toLowerCase()) ||
    profile.email.toLowerCase().includes(participantSearch.toLowerCase())
  );

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a meeting title');
      return;
    }

    if (!formData.observee_id) {
      toast.error('Please select a participant');
      return;
    }

    if (formData.meeting_type === 'custom' && !formData.custom_meeting_type.trim()) {
      toast.error('Please enter a custom meeting type');
      return;
    }

    if (!formData.scheduled_time) {
      toast.error('Please select a time');
      return;
    }

    setSaving(true);
    try {
      // Combine date and time
      const scheduledDateTime = new Date(formData.scheduled_date);
      const [hours, minutes] = formData.scheduled_time.split(':');
      scheduledDateTime.setHours(parseInt(hours), parseInt(minutes));

      // Get user's district_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('district_id')
        .eq('id', user?.id)
        .single();

      const meetingData = {
        title: formData.title,
        meeting_type: formData.meeting_type === 'custom' ? formData.custom_meeting_type : formData.meeting_type,
        observer_id: user?.id,
        observee_id: formData.observee_id,
        district_id: profile?.district_id,
        scheduled_date: scheduledDateTime.toISOString(),
        location: formData.location || null,
        description: formData.description || null,
        follow_up_required: formData.follow_up_required,
        follow_up_date: formData.follow_up_date?.toISOString() || null,
        status: 'scheduled'
      };

      if (editMeeting) {
        const { error } = await supabase
          .from('meetings')
          .update(meetingData)
          .eq('id', editMeeting.id);
        if (error) throw error;
        toast.success('Meeting updated successfully');
      } else {
        const { data: newMeeting, error } = await supabase
          .from('meetings')
          .insert(meetingData)
          .select()
          .single();
        if (error) throw error;
        
        // Create notification for the observee
        await supabase
          .from('notifications')
          .insert({
            user_id: formData.observee_id,
            title: 'New Meeting Scheduled',
            message: `${formData.title} has been scheduled for ${format(scheduledDateTime, 'PPP')} at ${formData.scheduled_time}`,
            type: 'meeting',
            read: false
          });
        
        toast.success('Meeting scheduled successfully');
      }

      onScheduled();
    } catch (error) {
      console.error('Error saving meeting:', error);
      toast.error('Failed to save meeting');
    } finally {
      setSaving(false);
    }
  };

  const meetingTypes = [
    { value: 'observation', label: 'Classroom Observation' },
    { value: 'mentoring', label: 'Mentoring Session' },
    { value: 'coaching', label: 'Coaching Meeting' },
    { value: 'evaluation', label: 'Performance Evaluation' },
    { value: 'feedback', label: 'Feedback Session' },
    { value: 'custom', label: 'Custom Meeting Type' }
  ];

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5" />
            {editMeeting ? 'Edit Meeting' : 'Schedule New Meeting'}
          </DialogTitle>
          <DialogDescription>
            {editMeeting ? 'Update the meeting details below.' : 'Fill in the meeting details to schedule a new observation or mentoring session.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Top Section - Main Form Fields */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <Label htmlFor="title">Meeting Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="e.g., Math Lesson Observation"
              />
            </div>

            <div>
              <Label>Meeting Type *</Label>
              <Select 
                value={formData.meeting_type} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, meeting_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meetingTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {formData.meeting_type === 'custom' && (
                <div className="mt-2">
                  <Input
                    value={formData.custom_meeting_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, custom_meeting_type: e.target.value }))}
                    placeholder="Enter custom meeting type"
                  />
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <div className="relative">
              <Label>Participant *</Label>
              <Input
                value={participantSearch}
                onChange={(e) => {
                  setParticipantSearch(e.target.value);
                  if (e.target.value.trim() === '') {
                    setSelectedParticipant(null);
                    setFormData(prev => ({ ...prev, observee_id: '' }));
                  }
                }}
                onFocus={() => {
                  if (participantSearch.trim().length >= 2) {
                    setShowParticipantResults(true);
                  }
                }}
                placeholder="Type to search participants..."
                className="w-full"
              />
              
              {showParticipantResults && filteredProfiles.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {filteredProfiles.map((profile) => (
                    <div
                      key={profile.id}
                      className="px-4 py-2 hover:bg-accent cursor-pointer text-sm"
                      onClick={() => handleParticipantSelect(profile)}
                    >
                      <div className="font-medium">
                        {profile.first_name} {profile.last_name}
                      </div>
                      <div className="text-muted-foreground text-xs">
                        {profile.email}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {participantSearch.trim().length >= 2 && filteredProfiles.length === 0 && showParticipantResults && (
                <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-4 text-sm text-muted-foreground">
                  No participants found matching "{participantSearch}"
                </div>
              )}
              
              {participantSearch.trim().length > 0 && participantSearch.trim().length < 2 && (
                <div className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg p-4 text-sm text-muted-foreground">
                  Type at least 2 characters to search
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="time">Time *</Label>
              <Input
                id="time"
                type="time"
                value={formData.scheduled_time}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_time: e.target.value }))}
              />
            </div>
          </div>

          {/* Calendar Section */}
          <div className="space-y-3">
            <Label className="text-base font-medium">Select Date *</Label>
            <div className="flex justify-center">
              <div className="border rounded-lg p-4 bg-card">
                <Calendar
                  mode="single"
                  selected={formData.scheduled_date}
                  onSelect={(date) => {
                    if (date) {
                      setFormData(prev => ({ ...prev, scheduled_date: date }));
                    }
                  }}
                />
              </div>
            </div>
          </div>

          {/* Additional Fields */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                placeholder="e.g., Room 205, Virtual (Zoom), etc."
              />
            </div>

            <div>
              <Label htmlFor="description">Description/Agenda</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Meeting objectives, topics to cover, etc."
                rows={3}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="follow-up"
                checked={formData.follow_up_required}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, follow_up_required: checked }))
                }
              />
              <Label htmlFor="follow-up">Follow-up meeting required</Label>
            </div>

            {formData.follow_up_required && (
              <div>
                <Label>Follow-up Date</Label>
                <Popover open={showFollowUpCalendar} onOpenChange={setShowFollowUpCalendar}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {formData.follow_up_date ? format(formData.follow_up_date, 'PPP') : 'Select follow-up date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={formData.follow_up_date || undefined}
                      onSelect={(date) => {
                        setFormData(prev => ({ ...prev, follow_up_date: date || null }));
                        setShowFollowUpCalendar(false);
                      }}
                      initialFocus
                      className="p-3"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : editMeeting ? 'Update Meeting' : 'Schedule Meeting'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}